import Message from '../components/Message';
import React, { useState, useEffect } from 'react';
import { Pencil, Plus, X } from 'lucide-react';
import api from '../api/api';
import { Confirm, ErrorState, TableSkeleton, Modal } from '../components';
import { PageHeader } from '../components/layout';
import PersonLeaveEditor, { editableLeavePeriods, serializeLeavePeriods, personLeaveStatus } from '../components/PersonLeaveEditor';

const saveErrorMessage = (error) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(item => item.msg).join('；');
  return typeof detail === 'string' ? detail : error.message;
};

const PersonsPage = () => {
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [editingPerson, setEditingPerson] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');

  const [newPerson, setNewPerson] = useState({
    name: '',
    account_id: '',
    email: '',
    department: '',
    weight: 1,
    is_active: true,
    description: '',
    leave_periods: []
  });

  // 弹窗状态
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });

  useEffect(() => {
    fetchPersons();
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const fetchPersons = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getPersons();
      const data = response.data;
      if (Array.isArray(data)) {
        setPersons(data);
      } else if (data && Array.isArray(data.persons)) {
        setPersons(data.persons);
      } else {
        setPersons([]);
        console.warn('API返回的人员数据不是数组:', data);
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setPersons([]);
      setLoading(false);
    }
  };

  const handleAddPerson = async (e) => {
    e.preventDefault();
    if (saving) return;
    setFormError(null);
    try {
      const leave_periods = serializeLeavePeriods(newPerson.leave_periods);
      setSaving(true);
      await api.createPerson({ ...newPerson, leave_periods });
      setNewPerson({ name: '', account_id: '', email: '', department: '', weight: 1, is_active: true, description: '', leave_periods: [] });
      setShowAddModal(false);
      fetchPersons();
    } catch (err) {
      setFormError(saveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePerson = async (e) => {
    e.preventDefault();
    if (!editingPerson || saving) return;
    setFormError(null);

    try {
      const leave_periods = serializeLeavePeriods(editingPerson.leave_periods);
      setSaving(true);
      await api.updatePerson(editingPerson.id, { ...editingPerson, leave_periods });
      setShowEditModal(false);
      setEditingPerson(null);
      fetchPersons();
    } catch (err) {
      setFormError(saveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const togglePersonStatus = async (person) => {
    try {
      const newStatus = !person.is_active;
      await api.updatePerson(person.id, { is_active: newStatus });
      setPersons(persons.map(p => p.id === person.id ? { ...p, is_active: newStatus } : p));
    } catch (err) {
      setError(err.message);
    }
  };

  const deletePerson = async (personId) => {
    setConfirmState({
      isOpen: true,
      message: '确定要删除这个人员吗？',
      onConfirm: async () => {
        try {
          await api.deletePerson(personId);
          setPersons(persons.filter(person => person.id !== personId));
        } catch (err) {
          setError(err.message);
        }
        setConfirmState({ ...confirmState, isOpen: false });
      }
    });
  };

  const openEditModal = (person) => {
    setFormError(null);
    setEditingPerson({ ...person, leave_periods: editableLeavePeriods(person.leave_periods || []) });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    if (saving) return;
    setFormError(null);
    setShowEditModal(false);
    setEditingPerson(null);
  };

  const openAddModal = () => {
    setNewPerson({ name: '', account_id: '', email: '', department: '', weight: 1, is_active: true, description: '', leave_periods: [] });
    setFormError(null);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (saving) return;
    setFormError(null);
    setShowAddModal(false);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortedPersons = () => {
    return [...persons].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: '4px', fontSize: '10px' }}>⇅</span>;
    return <span style={{ marginLeft: '4px', fontSize: '10px' }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  if (error) {
    return (
      <ErrorState
        title="无法加载人员目录"
        description={error}
        onRetry={fetchPersons}
      />
    );
  }

  return (
    <div className="configuration-list-page persons-directory-page">
      <Message message={formError} type="error" />
      <PageHeader
        title="人员管理"
        actions={<button type="button" className="btn btn-primary" onClick={openAddModal}><Plus size={16} aria-hidden="true" />添加人员</button>}
      />

      {/* 人员列表 */}
      {loading ? (
        <div className="persons-table-skeleton">
          <TableSkeleton rowCount={8} columnCount={7} label="加载人员目录中" />
        </div>
      ) : (
        <section className="configuration-list-surface persons-directory-surface">
          <header className="configuration-list-surface-header">
            <div>
              <h2>人员目录</h2>
              <p>共 {persons.length} 人，启用状态即时生效。</p>
            </div>
            <span className="configuration-list-count">{persons.filter(person => person.is_active).length} 已启用</span>
          </header>
          <div className="configuration-list-surface-body">
            <div className="table-container persons-config-table-wrap" style={{ border: 'none', borderRadius: 0 }}>
              <table className="table persons-config-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>ID{renderSortIcon('id')}</th>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>姓名{renderSortIcon('name')}</th>
                    <th onClick={() => handleSort('account_id')} style={{ cursor: 'pointer' }}>账户ID{renderSortIcon('account_id')}</th>
                    <th onClick={() => handleSort('department')} style={{ cursor: 'pointer' }}>部门{renderSortIcon('department')}</th>
                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>邮箱{renderSortIcon('email')}</th>
                    <th onClick={() => handleSort('is_active')} style={{ cursor: 'pointer', width: '100px' }}>状态{renderSortIcon('is_active')}</th>
                    <th style={{ width: '150px' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {persons.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: 'var(--space-10)' }}>
                        <div className="empty-state">
                          <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                          <div>暂无人员数据</div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    getSortedPersons().map(person => (
                      <tr key={person.id}>
                        <td>{person.id}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{person.name}</div>
                          {personLeaveStatus(person.leave_periods, now) && <span className="badge badge-info">{personLeaveStatus(person.leave_periods, now)}</span>}
                          {person.description && (
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                              {person.description.substring(0, 50)}{person.description.length > 50 ? '...' : ''}
                            </div>
                          )}
                        </td>
                        <td>
                          <code className="person-account-id" title={person.account_id} style={{
                            background: 'var(--bg-tertiary)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {person.account_id}
                          </code>
                        </td>
                        <td>
                          {person.department ? (
                            <span className="badge badge-info">{person.department}</span>
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>-</span>
                          )}
                        </td>
                        <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                          {person.email || '-'}
                        </td>
                        <td>
                          <label className="switch" title={person.is_active ? '点击禁用' : '点击启用'}>
                            <input
                              type="checkbox"
                              checked={person.is_active}
                              onChange={() => togglePersonStatus(person)}
                            />
                            <span className="switch-slider"></span>
                          </label>
                        </td>
                        <td>
                          <div className="table-action-icon-list">
                            <button type="button" className="table-action-icon-button" onClick={() => openEditModal(person)} aria-label="编辑人员" title="编辑人员"><Pencil size={17} aria-hidden="true" /></button>
                            <button type="button" className="table-action-icon-button is-danger" onClick={() => deletePerson(person.id)} aria-label="删除人员" title="删除人员"><X size={19} aria-hidden="true" /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="persons-mobile-list responsive-operation-list">
              {persons.length === 0 && <div className="empty-state">暂无人员数据，点击“添加人员”创建。</div>}
              {getSortedPersons().map(person => (
                <article className="responsive-operation-row" key={person.id}>
                  <div className="responsive-operation-main">
                    <strong>{person.name}</strong>
                    {personLeaveStatus(person.leave_periods, now) && <span className="badge badge-info">{personLeaveStatus(person.leave_periods, now)}</span>}
                    <span>{person.account_id}</span>
                    <small>{person.email || person.department || '未设置部门与邮箱'}</small>
                  </div>
                  <div className="responsive-operation-meta">
                    {person.department ? <span className="badge badge-info">{person.department}</span> : <span>-</span>}
                    <label className="switch" title={person.is_active ? '点击禁用' : '点击启用'}>
                      <input
                        type="checkbox"
                        checked={person.is_active}
                        onChange={() => togglePersonStatus(person)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                    <div className="persons-mobile-actions">
                      <button type="button" className="table-action-icon-button" onClick={() => openEditModal(person)} aria-label="编辑人员" title="编辑人员"><Pencil size={17} aria-hidden="true" /></button>
                      <button type="button" className="table-action-icon-button is-danger" onClick={() => deletePerson(person.id)} aria-label="删除人员" title="删除人员"><X size={19} aria-hidden="true" /></button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {showAddModal && (
        <Modal isOpen title={<>添加人员</>} onClose={closeAddModal} busy={saving} size="medium" className="admin-standard-dialog person-edit-modal">
<p>创建后可用于工单分配和分配池权重计算。</p>

          <form onSubmit={handleAddPerson}>
            <div className="modal-body">
            <div className="person-form-grid">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="person-create-name">姓名 *</label>
                <input
                  type="text"
                  id="person-create-name" disabled={saving} value={newPerson.name}
                  onChange={(e) => setNewPerson({...newPerson, name: e.target.value})}
                  className="form-input"
                  placeholder="人员姓名"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="person-create-account_id">账户ID *</label>
                <input
                  type="text"
                  id="person-create-account_id" disabled={saving} value={newPerson.account_id}
                  onChange={(e) => setNewPerson({...newPerson, account_id: e.target.value})}
                  className="form-input"
                  placeholder="Jira账户ID"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="person-create-email">邮箱</label>
                <input
                  type="email"
                  id="person-create-email" disabled={saving} value={newPerson.email}
                  onChange={(e) => setNewPerson({...newPerson, email: e.target.value})}
                  className="form-input"
                  placeholder="邮箱地址"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="person-create-department">部门</label>
                <input
                  type="text"
                  id="person-create-department" disabled={saving} value={newPerson.department}
                  onChange={(e) => setNewPerson({...newPerson, department: e.target.value})}
                  className="form-input"
                  placeholder="所属部门"
                />
              </div>
            </div>
            <PersonLeaveEditor periods={newPerson.leave_periods} disabled={saving}
              onChange={leave_periods => setNewPerson({ ...newPerson, leave_periods })} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  disabled={saving} checked={newPerson.is_active}
                  onChange={(e) => setNewPerson({...newPerson, is_active: e.target.checked})}
                />
                <span>启用</span>
              </label>
            </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={closeAddModal} disabled={saving}>取消</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '添加中…' : '添加人员'}</button>
            </div>
          </form>

        </Modal>
      )}

      {/* 编辑弹窗 */}
      {showEditModal && editingPerson && (
        <Modal isOpen title={<>编辑人员</>} onClose={closeEditModal} busy={saving} size="medium" className="admin-standard-dialog person-edit-modal">


            <form onSubmit={handleUpdatePerson}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">账户ID</label>
                  <code style={{
                    display: 'block',
                    background: 'var(--bg-tertiary)',
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '14px'
                  }}>
                    {editingPerson.account_id}
                  </code>
                  <small style={{ color: 'var(--text-muted)' }}>账户ID不可修改</small>
                </div>
                <div className="form-group">
                  <label className="form-label">姓名 *</label>
                  <input
                    type="text"
                    value={editingPerson.name}
                    onChange={(e) => setEditingPerson({...editingPerson, name: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                <div className="person-form-grid">
                  <div className="form-group">
                    <label className="form-label">邮箱</label>
                    <input
                      type="email"
                      value={editingPerson.email || ''}
                      onChange={(e) => setEditingPerson({...editingPerson, email: e.target.value})}
                      className="form-input"
                      placeholder="邮箱地址"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">部门</label>
                    <input
                      type="text"
                      value={editingPerson.department || ''}
                      onChange={(e) => setEditingPerson({...editingPerson, department: e.target.value})}
                      className="form-input"
                      placeholder="所属部门"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">描述</label>
                  <textarea
                    value={editingPerson.description || ''}
                    onChange={(e) => setEditingPerson({...editingPerson, description: e.target.value})}
                    className="form-input"
                    placeholder="人员描述（可选）"
                    rows="3"
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <PersonLeaveEditor periods={editingPerson.leave_periods} disabled={saving}
                  onChange={leave_periods => setEditingPerson({ ...editingPerson, leave_periods })} />

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editingPerson.is_active}
                      onChange={(e) => setEditingPerson({...editingPerson, is_active: e.target.checked})}
                    />
                    <span>启用人员</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={closeEditModal} disabled={saving}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? '保存中…' : '保存修改'}
                </button>
              </div>
            </form>

        </Modal>
      )}

      <Confirm
        isOpen={confirmState.isOpen}
        title="确认删除"
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={() => setConfirmState({ ...confirmState, isOpen: false })}
      />
    </div>
  );
};

export default PersonsPage;
